import { Link } from 'react-router-dom';
import { HeroImage } from './HeroImage';
import { SiteFooter } from './SiteFooter';
import { useTheme } from '../../store/auth';

/**
 * Bühne für alle Masken rund um den Zugang.
 *
 * Aus `Login` herausgelöst, als Bestätigung und Passwortwechsel dazukamen: Drei
 * weitere Seiten mit demselben Titelbild, demselben Schleier und derselben
 * Glasfläche wären dreimal dieselben vierzig Zeilen gewesen – und beim nächsten
 * Feinschliff am Bildausschnitt hätte man sie alle gefunden haben müssen.
 *
 * Die Anmeldung setzt ihre Karte unter den Schriftzug im Bild (`anchor="hero"`),
 * die kürzeren Masken stehen mittig. Der Unterschied ist der Grund für die
 * Aufteilung: Eine mittige Karte läge auf der Anmeldeseite genau auf dem
 * Schriftzug.
 */
export function AuthStage({
  children,
  anchor = 'center',
}: {
  children: React.ReactNode;
  anchor?: 'center' | 'hero';
}) {
  const { dark, toggle } = useTheme();

  return (
    <div className="relative isolate flex min-h-dvh flex-col overflow-hidden bg-[#04070f] sm:h-dvh">
      {/*
        Bild und Schleier in einer eigenen Ebene, `fixed` statt `absolute`: Am
        Dokument verankert wüchse der Grund mit der Seite, sobald das Formular
        höher wird als der Schirm, und der Bildausschnitt änderte sich beim
        Umschalten der Maske.
      */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0">
        {/*
          Auf dem Handy passt das ganze Titelbild in die Breite und sitzt oben;
          die Karte liegt darunter. Erst ab `sm` wird es wie zuvor beschnitten.
        */}
        <HeroImage
          priority
          pictureClassName="absolute inset-0 block"
          className="h-full w-full object-contain object-top sm:!object-cover sm:[object-position:50%_50%]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(46rem 30rem at 50% 78%, rgba(4,7,15,0.88), rgba(4,7,15,0.35) 56%, transparent 76%),' +
              'linear-gradient(to bottom, rgba(4,7,15,0.28) 0%, rgba(4,7,15,0.02) 22%, rgba(4,7,15,0.30) 55%, rgba(4,7,15,0.78) 82%, rgba(4,7,15,0.96) 100%)',
          }}
        />
        <div className="absolute inset-0 bg-[#04070f]/55 sm:hidden" />

        {/*
          „2.0“ hinter dem Schriftzug „SIMULATOR“ im Bild. Das Bild ist ein
          Festbild und lässt sich nicht umbeschriften, deshalb liegt die Zahl als
          eigene Ebene darüber. Der Rahmen bildet die Geometrie von `object-cover`
          mit `object-position: 50% 22%` nach: Bildgröße, Versatz und Schrift
          skalieren damit mit dem Schirm. Maße gelten für 1536×1024 (COMPANY
          endet bei rund 68,6 % der Breite, 34,8 % der Höhe).
        */}
        {(() => {
          const badge = (
            <span
              className="absolute font-black italic leading-none tracking-tight"
              style={{
                left: '68.6%',
                top: '34.8%',
                transform: 'translateY(-50%)',
                marginLeft: '0.3em',
                /* Metall wie beim Logo: heller Verlauf im Glyph, Verlauf nach unten dunkler. */
                background: 'linear-gradient(180deg, #ffffff 0%, #bfe0ff 42%, #3f86ea 52%, #1d4fa8 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                /*
                  Tiefe per gestapelter Schatten-Ebenen: `filter` statt `text-shadow`,
                  weil text-shadow unter dem transparenten Glyph durchscheint und den
                  Verlauf verschmutzt. Die Kante zeigt nach unten-rechts wie beim Logo.
                */
                filter:
                  'drop-shadow(0 1px 0 #0f3272) drop-shadow(0 2px 0 #0d2b62) drop-shadow(0 3px 0 #0a2352) drop-shadow(0 5px 6px rgba(0,0,0,0.55))',
              }}
            >
              2.0
            </span>
          );
          return (
            <>
              {/* Handy: Bild oben in voller Breite, also Rahmen = Bildbreite. */}
              <div
                className="absolute left-0 top-0 w-screen sm:hidden"
                style={{ height: 'calc(100vw / 1.5)', fontSize: 'calc(100vw / 22)' }}
              >
                {badge}
              </div>
              {/* Ab `sm`: Bild wie `object-cover` beschnitten, Rahmen wie dort berechnet. */}
              <div
                className="absolute hidden sm:block"
                style={{
                  width: 'max(100vw, 150vh)',
                  height: 'calc(max(100vw, 150vh) / 1.5)',
                  left: 'calc((100vw - max(100vw, 150vh)) / 2)',
                  top: 'calc((100vh - max(100vw, 150vh) / 1.5) * 0.5)',
                  fontSize: 'calc(max(100vw, 150vh) / 30)',
                }}
              >
                {badge}
              </div>
            </>
          );
        })()}
      </div>

      <div className="relative z-10 flex flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 px-4 py-4 sm:justify-end sm:px-7 sm:py-5">
          {/*
            Nur auf schmalen Schirmen: Dort ist der Schriftzug im Titelbild
            angeschnitten und der Kopf die einzige Kennzeichnung der Seite.
          */}
          <Link to="/login" className="flex items-center gap-2.5 sm:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-600 text-sm font-bold text-white shadow-lg shadow-accent-900/40">
              HS
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">Hosting-Simulator</p>
              <p className="font-mono text-2xs uppercase tracking-[0.18em] text-white/55">Control Panel</p>
            </div>
          </Link>

          <button
            type="button"
            onClick={toggle}
            aria-label="Farbschema wechseln"
            className="rounded-md border border-white/15 bg-white/10 px-2.5 py-1.5 text-sm text-white/80 backdrop-blur transition-colors hover:bg-white/20 hover:text-white"
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        <main
          className={
            anchor === 'hero'
              ? 'flex flex-1 items-start justify-center px-4 pb-6 pt-[13.5rem] sm:pb-0 sm:pt-[calc(50vh+8px-4.5rem-max(100vw/1536,100vh/1024)*47)]'
              : 'flex flex-1 items-center justify-center px-4 py-6'
          }
        >
          {/*
            Auf niedrigen Bildschirmen wird die Karte verkleinert, damit Karte und
            Fußzeile ohne Scrollen auf die Seite passen. Eigene Regel statt Klasse:
            die Variante `max-[…]` wird hier nicht zuverlässig erzeugt.
          */}
          <style>{`
            @media (min-width: 640px) and (max-height: 820px) { .auth-card { zoom: 0.85; } }
            @media (min-width: 640px) and (max-height: 700px) { .auth-card { zoom: 0.78; } }
          `}</style>
          <div className="auth-card w-full max-w-[24rem] animate-fade-in-up motion-reduce:animate-none">{children}</div>
        </main>

        <SiteFooter />
      </div>
    </div>
  );
}

/** Die Glasfläche selbst – Kopfzeile mit Statuspunkt, darunter der Inhalt. */
export function GlassPanel({
  title,
  children,
  onSubmit,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  onSubmit?: (event: React.FormEvent) => void;
  footer?: React.ReactNode;
}) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-black/10 bg-black/[0.03] px-5 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
        <span className="flex items-center gap-2 font-mono text-2xs uppercase tracking-[0.2em] text-panel-700 dark:text-white/60">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-pulse-soft rounded-full bg-accent-400 motion-reduce:animate-none" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
          </span>
          Zugang
        </span>
        <span className="font-mono text-2xs uppercase tracking-[0.2em] text-panel-500 dark:text-white/35">{title}</span>
      </div>
      <div className="space-y-2 px-5 py-2.5">{children}</div>
      {footer}
    </>
  );

  const className = [
    'overflow-hidden rounded-xl border',
    'border-black/10 bg-white/[0.82] dark:border-white/[0.16] dark:bg-[#081426]/80',
    'backdrop-blur-2xl backdrop-saturate-150',
    // Beide Schatten in einer Angabe: Als zwei Klassen würden sie dieselbe
    // Eigenschaft setzen und einander aufheben.
    'shadow-[0_28px_80px_-24px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.10)]',
  ].join(' ');

  return onSubmit ? (
    <form onSubmit={onSubmit} className={className}>
      {inner}
    </form>
  ) : (
    <div className={className}>{inner}</div>
  );
}

/**
 * Eingabefeld für die Glasfläche.
 *
 * Eigenständig, weil `Input` aus der Komponentensammlung eine deckende Fläche
 * mitbringt, die die Glaswirkung zunichtemachen würde.
 */
export function GlassField({
  label,
  value,
  onChange,
  hint,
  ...props
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-2xs uppercase tracking-[0.16em] text-panel-700 dark:text-white/45">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[
          'w-full rounded-lg border px-3 py-2 text-sm',
          'border-black/10 bg-black/[0.03] text-panel-900 dark:border-white/10 dark:bg-white/[0.06] dark:text-white',
          'placeholder:text-panel-400 dark:placeholder:text-white/25',
          'transition-colors hover:border-black/20 dark:hover:border-white/20',
          'focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500/60 dark:focus:border-accent-400/60 dark:focus:bg-white/[0.09]',
        ].join(' ')}
        {...props}
      />
      {hint && <span className="mt-1.5 block text-xs text-panel-600 dark:text-white/40">{hint}</span>}
    </label>
  );
}

/** Textlink im Fuß der Glasfläche, etwa „Passwort vergessen?“. */
export function GlassLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="text-xs text-panel-600 underline-offset-2 transition-colors hover:text-panel-900 hover:underline dark:text-white/45 dark:hover:text-white"
    >
      {children}
    </Link>
  );
}
